import { cacheGenerationKey, getCacheEpoch, isCacheFresh, saveChatList } from "../utils/offlineCache";

// Decision rule for offline encounter-list hydration (spec §2): the IDB cache
// is a fallback, never an overwrite — native-injected chats (Login.js
// gotAuthToken cold-start path) and already-loaded lists win.
export const shouldHydrateFromCache = ({ isServerAvailable, currentChatCount }) => !isServerAvailable && currentChatCount === 0;

// Decision rule for PERSISTING the fetched window. refreshList runs for every
// list query — including per-keystroke searches and group-scoped views — and
// saveChatList replaces the whole scope. Persisting a narrowed response would
// shrink the offline window to that subset, and a search that matched nothing
// would erase it outright. Only the unfiltered default window may be cached:
// no search term and no group scoping. The remaining view toggles
// (viewActive/viewArchived/...) are the user's own persistent default list
// view, so caching what they actually see is correct.
export const shouldPersistChatWindow = ({ searchTerm, filters } = {}) => {
  const hasSearch = typeof searchTerm === "string" && searchTerm.trim() !== "";
  const hasGroup = !!(filters && filters.group);
  return !hasSearch && !hasGroup;
};

// A cached row states an owner only when assignedTo actually names someone; an
// empty array is the same "says nothing" as an absent key. Exported so the
// offline assign gate (ensureAssignedToMe) asks the identical question the
// hydration asked -- otherwise a row this treats as ownerless could read as
// "states an owner" there, slip the refusal, and reach the transfer path with
// nobody named.
export const statesOwner = (row) => (Array.isArray(row?.assignedTo) ? row.assignedTo.length > 0 : !!row?.assignedTo);

// Both shapes, matching ensureAssignedToMe's own tolerance: a row keyed the
// other way must not read as a different encounter.
const rowId = (row) => row?.id || row?.chatId;

// An encounter created while the server was unreachable lives under a
// client-minted `temp-<epoch>` id until the native replay files it for real.
export const TEMP_CHAT_ID_PREFIX = "temp-";

export const isTempChat = (row) => {
  const id = rowId(row);
  return typeof id === "string" && id.startsWith(TEMP_CHAT_ID_PREFIX);
};

// The DURABLE TWIN join key. usePostMessages' create replay stamps the temp id
// onto the real encounter it files:
//
//   encounter.contextVariables["local:offlineId"] = chat.chatId;   (usePostMessages.js)
//
// so a server row carrying that stamp IS the landed version of a temp row still
// pinned in the list, and the temp row must retire.
//
// Read tolerantly across three shapes on purpose. The write path uses the
// `local:`-prefixed key, and web/organization-chats is requested with `ctx=local:*`
// (api.getChats), which is the same projection every other local: var comes back
// under -- ChatList reads `contextVariables["local:visitTime"]`, Analytics reads
// `contextVariables["local:encounterType"]`, both off getChats rows. But this one
// var is documented to be stored FLAT: api.getChatsByTempId had to filter
// `offlineId = '...'` because the nested `contextVariables['local:offlineId']`
// path silently matched 0 rows (proven against live data 2026-07-17). That proves
// how it is INDEXED, not how it is PROJECTED, and guessing wrong here either
// duplicates a row forever or drops unsynced work. So accept every shape rather
// than bet on one -- and the syncedTempIds backstop below covers the case where
// the projection carries no stamp at all.
export const offlineIdOf = (row) => {
  const cv = row?.contextVariables || {};
  return cv["local:offlineId"] || cv.offlineId || row?.offlineId || null;
};

const uniqueById = (rows) => [...new Map(rows.map((row) => [rowId(row), row])).values()];

// Keep unsynced offline work in the encounter list across a server window
// replacement.
//
// refreshList used to do `setChats([...patients.data.chats])` -- a wholesale
// replacement. On reconnect the server window does not yet contain an encounter
// the clinician created offline (the native replay is still in flight), so the
// row VANISHED from the list, coming back only after replay plus the next
// refresh -- or never, if the replay failed. Unsynced local work disappearing
// because a server window landed is the one thing the offline list must never
// do, so temp- rows are carried over on top of the fetched window.
//
// A temp row retires when, and only when, we have positive evidence its work is
// no longer local-only:
//
//   1. its durable twin is in the fetched rows (offlineIdOf join, above); or
//   2. the replay acked it -- `syncedTempIds`, fed by the "offlineEncounterSynced"
//      event usePostMessages publishes when a temp- create lands (or is found
//      already landed by api.getChatsByTempId). This is the backstop for a row
//      projection that turns out not to carry the offlineId stamp at all: the
//      ack is a server-confirmed create, and the caller only applies this merge
//      on a successful server fetch, so the durable encounter demonstrably
//      exists by then; or
//   3. the fetched rows already contain something under the same id.
//
// Ordering: survivors first. A just-created encounter is the newest one, which
// is where the list's default sort puts it anyway (ChatList re-sorts by
// visitTime/createdOn descending), so this is consistent for consumers that
// re-sort AND for any that don't.
//
// `fetchedChats` is deduped by id and stripped of temp rows, which is what makes
// this safe to hand loadMore's already-concatenated list: the survivors are
// recomputed from `currentChats` and re-pinned exactly once, so an appended page
// can never duplicate a temp row.
export const mergeUnsyncedTempChats = (currentChats, fetchedChats, { syncedTempIds } = {}) => {
  const current = Array.isArray(currentChats) ? currentChats : [];
  const fetched = uniqueById(Array.isArray(fetchedChats) ? fetchedChats : []);

  const durable = fetched.filter((row) => !isTempChat(row));
  const landedTempIds = new Set(durable.map(offlineIdOf).filter(Boolean));
  const durableIds = new Set(durable.map(rowId).filter(Boolean));
  const acked = syncedTempIds instanceof Set ? syncedTempIds : new Set(syncedTempIds || []);

  const survivors = uniqueById(current.filter(isTempChat)).filter((row) => {
    const id = rowId(row);
    return !landedTempIds.has(id) && !acked.has(id) && !durableIds.has(id);
  });

  return [...survivors, ...durable];
};

// Short-lived memo of the ownership lookup.
//
// web/organization-chats omits assignedTo from EVERY row, so "hydrate the rows
// that don't state an owner" never skips anything: the lookup fired on every
// default-window refresh -- every filter toggle, every remount, every
// reconnect, ~50 times a session -- for data that is used only by the offline
// gate and is therefore 100% wasted while online. Remembering the answers for
// five minutes collapses a session's worth of repeats into one call per window,
// while still hydrating rows the memo has not seen.
//
// Misses are memoized too (as null): an encounter with nobody assigned is the
// common case, and re-asking about it every refresh is most of the cost.
//
// Accepted staleness: a chart reassigned server-side inside the window keeps
// its old owner in the CACHE (never in the live list, which is fetched fresh).
// The blast radius is the offline gate's fallback for up to five minutes.
//
// The window is stamped when it OPENS and is never renewed by a read, so
// continuous use cannot keep it alive forever.
//
// Its generation key is the cache epoch AND the cache scope (user|org). The
// epoch alone would only reset on an explicit logout, but encounter ids are
// keyed per row, not per org -- an org switch that does not route through
// logout would let one tenant's ownership answers stamp another tenant's cached
// window. Same scope string the rows are stored under, so the memo can never
// outlive the partition it belongs to.
const ASSIGNEE_MEMO_TTL_MS = 5 * 60 * 1000;
// A FACTORY, not a shared sentinel object. The old shared `EMPTY_MEMO` handed
// the same Map to every reset; its `key: null` cannot match a template-string
// key today, so it was latent rather than live -- but it is one refactor away
// from serving one tenant's ownership answers to another.
const freshMemo = () => ({ key: null, openedAt: 0, map: new Map() });
let assigneeMemo = freshMemo();

const currentAssigneeMemo = (scope) => {
  const key = cacheGenerationKey(scope);
  if (assigneeMemo.key !== key || !isCacheFresh(assigneeMemo.openedAt, ASSIGNEE_MEMO_TTL_MS)) {
    assigneeMemo = { key, openedAt: Date.now(), map: new Map() };
  }
  return assigneeMemo;
};

// Test helper: drop the memo without touching IndexedDB.
export const resetOwnershipHydrationMemo = () => {
  assigneeMemo = freshMemo();
};

// Stamp ownership onto a fetched chat window.
//
// web/organization-chats OMITS assignedTo from its row projection (api.js
// documents it; the dedicated "Get Encounters Assignees" process exists exactly
// to fill it back in). The offline assign gate (ensureAssignedToMe in
// useChats.js) falls back to this cached window when refreshChat cannot answer,
// and it correctly refuses any row that does not STATE an owner -- so an
// unhydrated cache can only ever fail closed, and the fallback is dead code.
//
// ONE bounded lookup per window, never one per row: the window is capped well
// under the backend's 500-id batch, and this runs on every default-window
// refresh.
//
// Returns { rows, answered, asOf }.
//
//   answered === false  <=>  rows needed hydration and the lookup did not
//                            produce one (aborted, failed, or an unusable body)
//   asOf                     the time a SERVER last stated these owners: the
//                            memo's open time when any answer came from it
//
// `answered` exists because an abort is indistinguishable from a failure at the
// api layer (getEncounterAssignees turns both into null) -- and aborts are
// ROUTINE here: the controller is recreated and aborted on every change of
// user | location | filters | searchTerm, i.e. on every search keystroke and
// filter toggle. Returning raw rows in that case let the caller replace a fully
// hydrated cache with an unhydrated one, and the offline gate then refuses
// EVERY encounter (it fails closed on a row that does not state an owner). That
// is not a data-safety bug, it is a silent workflow outage: offline editing just
// stops working until the next unfiltered refresh.
export const hydrateChatWindowOwnership = async (rows, getEncounterAssignees, scope, abortController) => {
  const list = Array.isArray(rows) ? rows : [];
  if (typeof getEncounterAssignees !== "function" || list.length === 0) return { rows: list, answered: true };

  const ids = list
    .filter((row) => !statesOwner(row))
    .map(rowId)
    .filter(Boolean);
  if (ids.length === 0) return { rows: list, answered: true };

  const memo = currentAssigneeMemo(scope);
  const missing = ids.filter((id) => !memo.map.has(id));
  let answered = true;

  if (missing.length > 0) {
    let assignees = null;
    try {
      assignees = await getEncounterAssignees(missing, abortController);
    } catch (e) {
      // The helper documents that it never rejects; if one ever does, treat it
      // as "we do not know". Never log `e` -- the request body carries raw
      // encounter ids.
      assignees = null;
    }
    // A failed lookup memoizes nothing, so the next refresh retries it rather
    // than caching the outage for five minutes.
    if (assignees && typeof assignees === "object" && !Array.isArray(assignees)) {
      missing.forEach((id) => memo.map.set(id, assignees[id] ?? null));
    } else {
      answered = false;
    }
  }

  if (memo.map.size === 0) return { rows: list, answered, asOf: memo.openedAt };

  const hydrated = list.map((row) => {
    if (statesOwner(row)) return row;
    const assignee = memo.map.get(rowId(row));
    if (!assignee) return row;
    // Array form, the shape every consumer already reads (target.assignedTo[0]).
    return { ...row, assignedTo: Array.isArray(assignee) ? assignee : [assignee] };
  });
  return { rows: hydrated, answered, asOf: memo.openedAt };
};

// Persist the fetched window WITH ownership hydrated onto it. Callers treat this
// as fire-and-forget: the live list must never wait on the cache.
//
// Options object rather than positional `now, abortController`: the previous
// signature was a transposition waiting to happen (every caller passed
// `undefined, abortController`, so a caller that ever supplied a real `now`
// would have been one argument away from handing the controller in as a
// timestamp).
//
// THREE reasons to skip the save, all the same reason: never replace a usable
// cache with a worse one.
//
//   aborted ....... a superseded refresh. Routine -- the controller is aborted
//                   on every search keystroke and filter toggle.
//   !answered ..... the lookup failed. Indistinguishable from an abort at the
//                   api layer, and it means exactly the same thing here.
//   epoch moved ... the logout purge. Hydration is a network round-trip, so a
//                   persist decided just before an explicit logout can land well
//                   after clearOfflineCaches has run, rewriting PHI onto a
//                   device whose user has left.
//
// The trade is fewer cache writes: a user who searches immediately on every
// launch may go a session without a refreshed offline window. That is strictly
// better than the old behavior, which replaced a usable cache with one the
// offline gate refuses on every row. A slightly older cache is a degradation;
// a cache that fails the gate everywhere is an outage.
//
// `fetchedAt` is stamped with the OWNERSHIP-AS-OF time, not the save time. The
// gate reads that stamp as proof that a server stated these owners within
// CACHED_OWNERSHIP_MAX_AGE_MS -- but ownership values can come entirely from the
// 5-minute memo with no server call in this refresh at all, so re-stamping with
// `now` silently extended the guarantee by up to the memo window. The memo is
// stamped when it OPENS and never renewed, so its open time is a sound lower
// bound on when the server said this.
export const persistChatWindowWithOwnership = async (
  scope,
  rows,
  getEncounterAssignees,
  { now, abortController, epoch = getCacheEpoch() } = {}
) => {
  const {
    rows: hydrated,
    answered,
    asOf,
  } = await hydrateChatWindowOwnership(rows, getEncounterAssignees, scope, abortController);
  if (abortController?.signal?.aborted) return false;
  if (!answered) return false;
  if (getCacheEpoch() !== epoch) return false;
  const savedAt = typeof asOf === "number" && asOf > 0 ? Math.min(now ?? Date.now(), asOf) : (now ?? Date.now());
  // Hand the PRE-hydration epoch down so the write itself re-checks it, rather
  // than trusting a comparison made before openLucasDb() and the transaction.
  return saveChatList(scope, hydrated, savedAt, { epoch });
};
